import { CombinedFlows, FlowDirection, TransactionTypes } from "./flow-types";

// Helper functions to reduce cognitive complexity
const hasMyPrincipals = (myPrincipals) => myPrincipals && myPrincipals.length > 0;

const hasOnBehalfOf = (onBehalfOf) => onBehalfOf?.id;

const isOnBehalfOfSelf = (onBehalfOf, identity) =>
	onBehalfOf?.id === identity?.organization?.id;

const isOnBehalfOfOther = (onBehalfOf, identity) =>
	onBehalfOf && onBehalfOf.id !== identity?.organization?.id;

// Flow resolvers for each combined flow type
const pushFlowResolvers = {
	[CombinedFlows.CONTRIBUTOR_ONLY]: () => CombinedFlows.CONTRIBUTOR_ONLY,
	[CombinedFlows.POB_ONLY]: () => CombinedFlows.POB_ONLY,
	[CombinedFlows.POBOP_ONLY]: () => CombinedFlows.POBOP_ONLY,

	[CombinedFlows.CONTRIBUTOR_POB]: (selections, identity) => {
		const { onBehalfOf } = selections;
		if (isOnBehalfOfSelf(onBehalfOf, identity)) return CombinedFlows.CONTRIBUTOR_ONLY;
		if (isOnBehalfOfOther(onBehalfOf, identity)) return CombinedFlows.POB_ONLY;
		return CombinedFlows.CONTRIBUTOR_POB;
	},

	[CombinedFlows.POB_POBOP]: (selections, identity) => {
		const { onBehalfOf, myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.POBOP_ONLY;
		if (hasOnBehalfOf(onBehalfOf) && isOnBehalfOfOther(onBehalfOf, identity)) {
			return CombinedFlows.POB_ONLY;
		}
		return CombinedFlows.POB_POBOP;
	},

	[CombinedFlows.CONTRIBUTOR_POBOP]: (selections) => {
		const { myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.POBOP_ONLY;
		return CombinedFlows.CONTRIBUTOR_ONLY;
	},

	[CombinedFlows.CONTRIBUTOR_POB_POBOP]: (selections, identity) => {
		const { onBehalfOf, myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.POBOP_ONLY;
		if (hasOnBehalfOf(onBehalfOf)) {
			return isOnBehalfOfOther(onBehalfOf, identity)
				? CombinedFlows.POB_ONLY
				: CombinedFlows.CONTRIBUTOR_ONLY;
		}
		return CombinedFlows.CONTRIBUTOR_POB_POBOP;
	},
};

const pullFlowResolvers = {
	[CombinedFlows.CONSUMER_ONLY]: () => CombinedFlows.CONSUMER_ONLY,
	[CombinedFlows.COB_ONLY]: () => CombinedFlows.COB_ONLY,
	[CombinedFlows.COBOP_ONLY]: () => CombinedFlows.COBOP_ONLY,

	[CombinedFlows.CONSUMER_COB]: (selections, identity) => {
		const { onBehalfOf } = selections;
		if (isOnBehalfOfSelf(onBehalfOf, identity)) return CombinedFlows.CONSUMER_ONLY;
		if (isOnBehalfOfOther(onBehalfOf, identity)) return CombinedFlows.COB_ONLY;
		return CombinedFlows.CONSUMER_COB;
	},

	[CombinedFlows.COB_COBOP]: (selections, identity) => {
		const { onBehalfOf, myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.COBOP_ONLY;
		if (hasOnBehalfOf(onBehalfOf) && isOnBehalfOfOther(onBehalfOf, identity)) {
			return CombinedFlows.COB_ONLY;
		}
		return CombinedFlows.COB_COBOP;
	},

	[CombinedFlows.CONSUMER_COBOP]: (selections) => {
		const { myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.COBOP_ONLY;
		return CombinedFlows.CONSUMER_ONLY;
	},

	[CombinedFlows.CONSUMER_COB_COBOP]: (selections, identity) => {
		const { onBehalfOf, myPrincipals } = selections;
		if (hasMyPrincipals(myPrincipals)) return CombinedFlows.COBOP_ONLY;
		if (hasOnBehalfOf(onBehalfOf)) {
			return isOnBehalfOfOther(onBehalfOf, identity)
				? CombinedFlows.COB_ONLY
				: CombinedFlows.CONSUMER_ONLY;
		}
		return CombinedFlows.CONSUMER_COB_COBOP;
	},
};

export function getActiveFlow(
	combinedFlow,
	selections,
	identity,
	transactionType = TransactionTypes.PUSH_STORE,
	flowDirection = FlowDirection.PUSH
) {
	const { onBehalfOf } = selections;

	// Handle PUSH_TO_AGENT transaction type
	if (transactionType === TransactionTypes.PUSH_TO_AGENT) {
		return isOnBehalfOfOther(onBehalfOf, identity)
			? CombinedFlows.POB_ONLY
			: CombinedFlows.CONTRIBUTOR_ONLY;
	}

	const isPullFlow = flowDirection === FlowDirection.PULL

	// Handle PULL_FROM_AGENT transaction type
	if (isPullFlow && transactionType === TransactionTypes.PULL_FROM_AGENT) {
		return isOnBehalfOfOther(onBehalfOf, identity) ? CombinedFlows.COB_ONLY : CombinedFlows.CONSUMER_ONLY;
	}

	const resolvers = isPullFlow ? pullFlowResolvers : pushFlowResolvers;

	const resolver = resolvers[combinedFlow];

	if (resolver) {
		return resolver(selections, identity);
	}

	return isPullFlow ? CombinedFlows.CONSUMER_ONLY : CombinedFlows.CONTRIBUTOR_ONLY;
}
